import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AnnotationOverlay } from '@/shared/annotations';
import { guestApiBaseUrl } from '../apiBaseUrl';
import { AuthedImage, PhotoLightbox } from '../ui';
import { preloadGuestImage } from '../ui/guestImageCache';
import type { ArrivalStepImage } from '../useGuestSession';
import { ArrowButton, GalleryDots, parseAnnotations } from './arrivalGalleryParts';

interface Props {
  images: ArrivalStepImage[];
  /** Guest-Bearer-Token — die Bild-URLs sind auth-geschuetzt (kein `<img src>` ohne Header). */
  token: string | undefined;
}

/** Ab hier werden Punkte zur Perlenkette — dann sagt die Zählung mehr als sie. */
const MAX_DOTS = 6;

/**
 * Bilder eines Schritts als Karussell. Die Markierungen liegen als Overlay ueber dem Original —
 * so bleibt das Foto unveraendert und der Pfeil sitzt auf jedem Display an derselben Stelle.
 *
 * Dass es mehrere Bilder gibt, muss man dem Bild ansehen: die Pfeile standen frueher klein
 * darunter und wurden auf dem Telefon uebersehen — dort erwartet man ausserdem zu wischen.
 */
export function ArrivalStepGallery({ images, token }: Props) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  // Ein Wegweiser-Foto ist auf einem Telefon vier Zentimeter breit — der Pfeil auf der Tür ist
  // darauf ein Punkt. Groß ziehen war bisher nur im Bilder-Bereich möglich, nicht hier, wo die
  // Bilder erklären sollen, wie man ins Haus kommt.
  const [gross, setGross] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  // Das nächste und das vorige Bild liegen schon bereit, wenn der Gast wischt.
  useEffect(() => {
    const count = images.length;
    if (count < 2 || !token) return;
    const at = Math.min(index, count - 1);
    for (const neighbour of [images[(at + 1) % count], images[(at - 1 + count) % count]]) {
      if (neighbour) preloadGuestImage(`${guestApiBaseUrl}${neighbour.urls.full}`, token);
    }
  }, [images, index, token]);
  if (images.length === 0) return null;

  const position = Math.min(index, images.length - 1);
  const current = images[position];
  if (!current) return null;
  const many = images.length > 1;
  const step = (delta: number) =>
    setIndex((value) => (value + delta + images.length) % images.length);

  return (
    <div className="mt-2">
      <div
        className="border-line bg-bg-elev relative overflow-hidden rounded-xl border"
        onTouchStart={(event) => {
          touchStartX.current = event.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(event) => {
          const from = touchStartX.current;
          touchStartX.current = null;
          const to = event.changedTouches[0]?.clientX;
          // 40 px, damit ein Tippen mit leicht wackelndem Finger kein Blaettern ausloest.
          if (!many || from === null || to === undefined || Math.abs(to - from) < 40) return;
          step(to < from ? 1 : -1);
        }}
      >
        <button
          type="button"
          onClick={() => setGross(position)}
          aria-label={t('stations.stay.arrival.gallery.enlarge')}
          className="block w-full cursor-zoom-in"
        >
          <AuthedImage
            url={`${guestApiBaseUrl}${current.urls.full}`}
            token={token}
            alt=""
            className="aspect-4/3 block w-full object-cover"
          />
        </button>
        {/* Über dem Knopf und ohne eigene Trefferfläche: die Markierungen gehören zum Bild, und
            ein Klick darauf soll dasselbe tun wie ein Klick daneben. */}
        <span className="pointer-events-none absolute inset-0">
          <AnnotationOverlay annotations={parseAnnotations(current.annotationsJson)} />
        </span>

        {many && (
          <>
            <ArrowButton
              side="left"
              label={t('stations.stay.arrival.gallery.prev')}
              onClick={() => step(-1)}
            >
              <ChevronLeft className="size-5" aria-hidden />
            </ArrowButton>
            <ArrowButton
              side="right"
              label={t('stations.stay.arrival.gallery.next')}
              onClick={() => step(1)}
            >
              <ChevronRight className="size-5" aria-hidden />
            </ArrowButton>
            <span className="bg-bg-dark/70 text-bg absolute right-2 top-2 rounded-full px-2 py-0.5 font-mono text-[11px] tabular-nums">
              {position + 1} / {images.length}
            </span>
          </>
        )}
      </div>

      {gross !== null && (
        <PhotoLightbox
          photos={images.map((image) => ({
            src: `${guestApiBaseUrl}${image.urls.full}`,
            alt: null,
            placeholder: `${guestApiBaseUrl}${image.urls.thumb}`,
          }))}
          index={gross}
          onClose={() => setGross(null)}
          onNavigate={setGross}
          closeLabel={t('stations.stay.arrival.gallery.close')}
          prevLabel={t('stations.stay.arrival.gallery.prev')}
          nextLabel={t('stations.stay.arrival.gallery.next')}
          failedLabel={t('stations.stay.arrival.gallery.failed')}
          retryLabel={t('stations.stay.arrival.gallery.retry')}
          token={token}
        />
      )}

      {many && images.length <= MAX_DOTS && (
        <GalleryDots
          count={images.length}
          position={position}
          onSelect={setIndex}
          labelOf={(number) => t('stations.stay.arrival.gallery.goTo', { number })}
        />
      )}

      <span className="sr-only" aria-live="polite">
        {t('stations.stay.arrival.gallery.position', {
          current: position + 1,
          total: images.length,
        })}
      </span>
    </div>
  );
}
